import React, { useState, useEffect } from "react";
import { Card, ListGroup, Button, Row, Col, Alert } from "react-bootstrap";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setPlan, selectSignUp } from "../features/signup/signupSlice";
import { Campaign, selectCampaign } from "../features/campaign/campaignSlice";
import {
  Discount,
  discountWording,
  selectDiscount,
} from "../features/discount/discountSlice";
import { Coupon } from "../features/coupon/couponAPI";
import {
  fetchDisclaimersAsync,
  selectPlansDisclaimer,
} from "../features/disclaimers/disclaimersSlice";
import { cardPrice } from "../lib/promo";
import { cardPoints, eveningSpeedsOf, speedPoint } from "../lib/cardPoints";
import { CardPoint, SpeedCardPoint } from "./CardPoint";
import { Plan } from "../models/plans";
import ScrollIndicator from "./ScrollIndicator";
import ScrollStack from "./ScrollStack";
import PineappleAlert from "./PineappleAlert";
import { humanizeNumber } from "../lib/utils";
import { trackPlanSelect } from "../lib/analytics";
import {
  selectHideEveningSpeedToggle,
  selectFreeTextDisclaimersEnabled,
  selectPromoPricing,
  selectPlanCardPoints,
} from "../features/featureFlags/featureFlagsSlice";
import MostPopularPlansSwitch from "./MostPopularPlansSwitch";
import "./PlanSpeedSwitch.css";

const criticalInfoLink =
  "https://www.pineapple.net.au/wp-content/uploads/PN_ResCIS.pdf";

interface MostPopularCircleProps {
  cost: number;
}

const MostPopularCircle: React.FC<MostPopularCircleProps> = ({ cost }) => (
  <div className="position-relative">
    <div
      style={{
        top: "-2.4em",
        right: "-1em",
        zIndex: 999,
      }}
      className="d-flex
        align-items-center
        align-middle
        bg-purple
        rounded-pill
        p-4
        w-auto
        position-absolute"
    >
      <span
        className="text-warning
    lh-1
    fs-5
    fw-bold
    position-absolute
    text-center
    start-0"
      >
        SAVE{" "}
        <strong className="fs-2" style={{ color: "white" }}>
          <sup
            style={{
              fontSize: "0.45em",
              top: "-0.7em",
              position: "relative",
              marginRight: "1px",
            }}
          >
            $
          </sup>
          {cost}
        </strong>
      </span>
    </div>
  </div>
);

const MostPopular = () => (
  <div className="position-relative">
    <div
      style={{
        borderWidth: "0.3em",
        borderStyle: "solid",
        borderColor: "primary !important",
        top: "-2.4em",
        right: "-1em",
        zIndex: 999,
        backgroundColor: "black",
      }}
      className="d-flex
        align-items-center
        align-middle
        rounded-pill
        p-4
        bg-black
        border-black
        w-auto
        position-absolute"
    >
      <span
        className="text-warning
          lh-1
          fs-5
          fw-bold
          position-absolute
          text-center
          start-0"
      >
        BLACK FRIDAY <strong style={{ color: "white" }}>SALE</strong>
      </span>
    </div>
  </div>
);

interface CampaignOfferProps {
  campaign: Campaign;
}

const CampaignOffer = ({ campaign }: CampaignOfferProps) => {
  const { monthsFree: n, terms, referral } = campaign;
  // The mark points to the terms under the plans, which the Plans
  // Disclaimer replaces.
  const freeTextDisclaimersEnabled = useAppSelector(
    selectFreeTextDisclaimersEnabled,
  );
  const icon = terms !== "" && !freeTextDisclaimersEnabled ? "⁺" : "";
  const boldCopy =
    n !== undefined
      ? `Plus ${humanizeNumber(n)} ${n > 1 ? "months" : "month"} FREE${icon}`
      : "Special Promotional Pricing";

  const supportingCopy =
    n !== undefined
      ? "applies to any of the below plans"
      : "applies in your building";

  return (
    <div className="d-flex justify-content-center">
      <PineappleAlert className="d-inline-block px-3">
        {referral && campaign.id === "SALES_REFERRAL" && (
          <>
            <strong>
              One Month FREE
              {icon}
            </strong>{" "}
            applies to any of the below plans.
          </>
        )}
        {referral && campaign.id !== "SALES_REFERRAL" && (
          <>
            Enjoy{" "}
            <strong>
              One Month FREE
              {icon}
            </strong>{" "}
            thanks to your neighbour. One Month FREE applies to any of the below
            plans.
          </>
        )}
        {!referral && (
          <>
            <strong className="text-capitalize">{boldCopy}</strong>{" "}
            {supportingCopy}
            <br />
            <span className={campaign.moveIn ? "d-none" : ""}>
              Hurry, offer ends {dayjs(campaign.endDate).format("DD/MM/YYYY")}
            </span>
          </>
        )}
      </PineappleAlert>
    </div>
  );
};

interface DiscountOfferProps {
  discount: Discount;
  coupon?: Coupon;
}

const DiscountOffer = ({ discount, coupon }: DiscountOfferProps) => {
  const words = discountWording(discount, true);
  const freeTextDisclaimersEnabled = useAppSelector(
    selectFreeTextDisclaimersEnabled,
  );
  const hasTerms = discount.terms !== "" && discount.terms !== undefined;
  const icon = hasTerms && !freeTextDisclaimersEnabled ? "⁺" : "";

  if (words === "") return null;
  if (coupon?.coupon_id === "PNBF24") return null;

  return (
    <div className="d-flex justify-content-center">
      <PineappleAlert className="d-inline-block px-3">
        <strong>
          {words}
          {icon}
          {coupon?.coupon_id && ` using promo code ${coupon.coupon_id}`}
          {discount.period !== 0 && ". Prices as marked."}
        </strong>
      </PineappleAlert>
    </div>
  );
};

interface PlanProps {
  plans: Record<string, Plan>;
  className?: string;
}

const Plans = ({ className = "", plans }: PlanProps) => {
  const dispatch = useAppDispatch();
  const signUpState = useAppSelector(selectSignUp);
  const campaign = useAppSelector(selectCampaign);
  const discount = useAppSelector(selectDiscount);
  const freeTextDisclaimersEnabled = useAppSelector(
    selectFreeTextDisclaimersEnabled,
  );
  const plansDisclaimer = useAppSelector(selectPlansDisclaimer);

  useEffect(() => {
    if (freeTextDisclaimersEnabled) dispatch(fetchDisclaimersAsync());
  }, [freeTextDisclaimersEnabled]);

  const plansState: Record<string, boolean> = Object.keys(plans).reduce(
    (acc, name) => ({
      ...acc,
      [name]: signUpState.plan.name === name,
    }),
    {},
  );

  const sortedPlans = Object.values(plans).sort(
    (p1, p2) => p1.speed - p2.speed,
  );

  // A 750 Mbps plan only exists in the NBN FTTP/HFC plan set (same marker the
  // critical-information link below relies on).
  const isFTTP = sortedPlans.some((p) => p.speed === 750);

  // Boost 100 is the DGtek default; a Community Fibre plan set has no such
  // name, so its entry tier is preselected instead.
  const defaultPlan = () =>
    sortedPlans.find((plan) => plan.name === "Boost 100")
    ?? sortedPlans.find((plan) => plan.network === 'communityfibre');

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const [buttonState, setButtonState] =
    useState<Record<string, boolean>>(plansState);
  const setChange = (plan: string) => {
    Object.keys(buttonState).forEach((planName) => {
      buttonState[planName] = false;
    });
    buttonState[plan] = !buttonState[plan];
    setButtonState({
      ...buttonState,
    });
    setSelectedIndex(sortedPlans.map((p) => p.name).indexOf(plan));
    dispatch(setPlan(plans[plan]));
  };

  // If the default plan changes such as through loading a different set of
  // sorted plans, trigger this effect again
  useEffect(() => {
    const p = defaultPlan();
    if (p !== undefined) setChange(p.name);
  }, [defaultPlan()]);

  const [mostPopular, setMostPopular] = useState(false);
  const hideEveningSpeedToggle = useAppSelector(selectHideEveningSpeedToggle);
  const promoPricing = useAppSelector(selectPromoPricing);
  const planCardPoints = useAppSelector(selectPlanCardPoints);

  // Per-card toggle shown on every plan: it flips the displayed figure between
  // the advertised plan speed and the typical evening speed (measured 7-11pm).
  // The evening figures come from the plan record maintained in the marketing
  // portal, resolved server-side for the building's access technology and sent
  // in evening_download / evening_upload; when a plan doesn't carry them yet,
  // fall back to the advertised speed (see eveningSpeedsOf). Display-only —
  // it never changes the selected plan, its price, or what is sent on sign up.
  const [eveningSpeed, setEveningSpeed] = useState<Record<string, boolean>>({});

  const nonPromo = `Following the 6-month promotional period, plans will revert
  to the advertised full retail price. Typical evening speeds are subject to
  change and are measured between 7-11pm. Speeds may vary due to external
  factors including in-building wiring, equipment, and number of devices.`;

  const normal = `3-months FREE and special offer pricing are available to new
  customers only and apply to the first 6 months of service. Offer ends 11.59pm
  AEDT, 19 July 2026. Promo Code EOFY3MF MUST be used and validated at the time
  of sign up for the discount to apply. Excludes router and static IP charges.
  After the 3-month free period, plans will be billed at the special offer
  pricing for the remaining 3 months as follows: 150/150Mbps - $59 per month,
  250/250Mbps - $69 per month, 500/500Mbps - $79 per month and 1000/1000Mbps -
  $89. Following the 6-month promotional period, plans will revert to the
  advertised full retail price. Typical evening speeds are subject to change
  and are measured between 7-11pm. Speeds may vary due to external factors
  including in- building wiring, equipment, and number of devices.`;
  const hfc = `3-months FREE and special offer pricing are available to new customers only
  and apply to the first 6 months of service. Offer ends 11.59pm AEDT, 19 July
  2026. Promo Code EOFY3MF MUST be used and validated at the time of sign up
  for the discount to apply. Excludes router and static IP charges. After the
  3-month free period, plans will be billed at the special offer pricing for
  the remaining 3 months as follows: 500/500Mbps - $59 per month, 750/750Mbps -
  $69 per month and 1000/1000Mbps - $79. Following the 6- month promotional
  period, plans will revert to the advertised full retail price. Typical
  evening speeds are subject to change and are measured between 7-11pm. Speeds
  may vary due to external factors including in- building wiring, equipment,
  and number of devices.`;

  return (
    <div className={className}>
      {campaign.id && <CampaignOffer campaign={campaign} />}
      {!campaign.id && (
        <DiscountOffer coupon={signUpState?.coupon} discount={discount} />
      )}

      {sortedPlans.length > 4 && (
        <MostPopularPlansSwitch
          options={["All Plans", "Most Popular"]}
          onChange={() => setMostPopular(!mostPopular)}
          checked={mostPopular}
        />
      )}
      <ScrollStack
        className="hide-scrollbar
          px-2
          py-4
          overflow-scroll
          d-flex
          align-items-stretch
          "
        gap={2}
        direction="horizontal"
        mostPopular={mostPopular}
        initialPosition={mostPopular ? "auto" : "offset-1"}
      >
        {(sortedPlans.length > 1
          ? sortedPlans.filter(
              (plan) =>
                ["streamer", "boost"].includes(plan.name.toLowerCase()) ||
                !mostPopular,
            )
          : sortedPlans
        ).flatMap((plan) => {
          const evening = !!eveningSpeed[plan.id];
          const { down, up } = eveningSpeedsOf(plan);
          const shownDown = evening ? down : (plan.download ?? plan.speed);
          const shownUp = evening ? up : (plan.upload ?? plan.speed);
          // Flipping to evening speed also swaps the title colour so the
          // change is obvious: green → orange for on-net buildings, orange →
          // green for NBN FTTP/HFC buildings.
          const planTierColor = isFTTP ? "text-orange" : "text-primary";
          const eveningColor = isFTTP ? "text-primary" : "text-orange";
          const titleColor = evening ? eveningColor : planTierColor;
          const activeLabel = "pn-switch-label-active fw-semibold";
          const planLabelCls = evening ? "text-muted" : activeLabel;
          const eveningLabelCls = evening ? activeLabel : "text-muted";
          const { price, promoCode } = cardPrice(plan, discount, promoPricing);
          return (
            <Card
              key={plan.id}
              className="pineapple-card shadow h-100"
              border="grey"
            >
              {/* (plan.id === 'pa-150mbps') && <MostPopular /> */}
              {/* <MostPopularCircle cost={(Number(plan?.cost) || 0) * 3} /> */}
              <Card.Body>
                <Card.Title
                  className={`text-center
                  ${titleColor}
                  border-bottom
                  border-primary
                  fs-3
                  pb-2
                  mb-0`}
                >
                  {plan.name}
                  <br />
                  {shownDown}/{shownUp} Mbps *
                </Card.Title>
                {/* Fixed min-height keeps the card from jumping when the label
                    reflows as it toggles between normal and emphasised. */}
                {!hideEveningSpeedToggle && (
                  <div
                    className="d-flex align-items-center justify-content-center gap-2 mt-3 mb-3 text-center fs-6"
                    style={{
                      lineHeight: 1.1,
                      minHeight: "2.25rem",
                    }}
                  >
                    <span
                      className={`pn-switch-label ${planLabelCls}`}
                      data-label="Plan speed"
                    >
                      Plan speed
                    </span>
                    <label
                      className="pn-switch"
                      htmlFor={`evening-speed-${plan.id}`}
                    >
                      <input
                        id={`evening-speed-${plan.id}`}
                        type="checkbox"
                        checked={evening}
                        onChange={() =>
                          setEveningSpeed((s) => ({
                            ...s,
                            [plan.id]: !s[plan.id],
                          }))
                        }
                        aria-label="Show typical evening speed"
                      />
                      <span className="pn-switch-slider" />
                    </label>
                    <span
                      className={`pn-switch-label ${eveningLabelCls}`}
                      data-label="Typical evening speed 7-11pm"
                    >
                      Typical evening speed 7-11pm
                    </span>
                  </div>
                )}
                <ListGroup
                  className="border-bottom border-primary py-2"
                  variant="flush"
                >
                  {cardPoints(plan, planCardPoints).map((point, i) => (
                    // Two points can have the same text, thus the key is
                    // the position.
                    // eslint-disable-next-line react/no-array-index-key
                    <CardPoint key={i}>{point}</CardPoint>
                  ))}
                  {planCardPoints && (
                    <SpeedCardPoint point={speedPoint(plan)} />
                  )}
                </ListGroup>
                <Card.Text className="text-center py-2 mb-0" />
                <Card.Text className="text-center">
                  <span
                    className="
                  display-1
                  text-primary
                  text-center
                  lh-1
                  d-block"
                  >
                    $
                    {price}
                    <span className="text-primary text-center align-top display-3">
                      ^
                    </span>
                  </span>
                  <span className="fs-6">per month</span>
                  {promoCode && (
                    <>
                      <br />
                      <strong className="fs-6">
                        with promo code {promoCode}
                      </strong>
                    </>
                  )}
                  <br />
                  <span className="fs-6">
                    {plan.description1 &&
                    plan.description1 !== undefined &&
                    plan.description1 !== "  " ? (
                      plan.description1
                    ) : (
                      <span>&nbsp;&nbsp;</span>
                    )}
                  </span>
                  {/* <PineappleAlert classOverride="text-white bg-purple my-2 text-center rounded-pill">
                  <div
                    style={{
                      paddingLeft: 20,
                      paddingRight: 20,
                      fontSize: 18,
                      color: "white",
                      paddingBottom: 4,
                      paddingTop: 4,
                    }}
                  >
                    Use code
                    <strong className="text-warning" style={{ fontSize: 18 }}>
                      {" "}
                      EOFY3MF{" "}
                    </strong>
                    to get
                    <strong> your first 3 months FREE! </strong>
                  </div>
                </PineappleAlert> */}
                </Card.Text>
                <Button
                  id={`select_plan_${plan.name}`}
                  className="w-100"
                  size="lg"
                  onClick={() => {
                    // Not in setChange, which also runs for the default plan
                    trackPlanSelect(plan);
                    setChange(plan.name);
                  }}
                  variant={buttonState[plan.name] ? "secondary" : "primary"}
                >
                  {buttonState[plan.name] ? "Selected" : "Select"}
                </Button>
              </Card.Body>
            </Card>
          );
        })}
      </ScrollStack>

      <Row className="justify-content-sm-center px-1">
        <Col sm md={8}>
          {freeTextDisclaimersEnabled && plansDisclaimer.trim() !== "" && (
            <div style={{ display: "flex", flexDirection: "row" }}>
              <span
                className="text-primary text-centre align-top display-6 mx-1"
              >
                ^
              </span>
              {/* From the marketing portal. Line breaks are kept. */}
              <p style={{ whiteSpace: "pre-line" }}>{plansDisclaimer}</p>
            </div>
          )}
          {!freeTextDisclaimersEnabled && (
            <div style={{ display: "flex", flexDirection: "row" }}>
              <span
                className="text-primary text-centre align-top display-6 mx-1"
              >
                ^
              </span>
              <p>
                {/* eslint-disable-next-line */}
                {/* sortedPlans?.find((p) => p?.speed === 750)?.id ? hfc :  normal */}
                {nonPromo}
                {discount.terms ? (
                  <>
                    <strong className="text-primary">+</strong>
                    <span> {discount.terms}</span>
                  </>
                ) : null}
              </p>
            </div>
          )}
          <center>
            <strong className="text-primary">*</strong>{" "}
            <a
              href={
                sortedPlans.find((p) => p.speed === 750)?.id
                  ? "PN_ResCIS_FTTP-HFC_Feb26.pdf"
                  : criticalInfoLink
              }
              target="_blank"
              rel="noreferrer"
            >
              {sortedPlans?.find((p) => p.speed === 750)?.id
                ? "Critical information - FTTP/HFC"
                : "Critical information"}
            </a>
          </center>
        </Col>
      </Row>
      <Row className="justify-content-sm-center">
        <Col sm md={4} className="justify-content-center d-flex">
          <ScrollIndicator
            activeIndex={selectedIndex}
            length={mostPopular ? 3 : Object.values(plans).length}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Plans;
